import classNames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import FormControl from "@material-ui/core/FormControl";
import styled from "styled-components";
import SharedRadioGroup from "Shared/Form/RadioGroup";
import { analyticsFEA } from "src/AnalyticsFEA";
import StepContent from "src/components/ContinueDeal/StepContent/StepContent";
import { BaseStepProps } from "src/components/ContinueDeal/CreditApp/shared/BaseStepProps";
import FormContainer from "src/components/ContinueDeal/CreditApp/shared/FormContainer";
import FormHeader from "src/components/ContinueDeal/CreditApp/shared/FormHeader";
import Label from "Shared/Form/Label";
import "./index.scss";
import { required } from "Shared/Form/validators";
import FieldError from "Shared/Form/FieldError";
import Select from "src/components/shared/Form/Select/index.tsx";
import { ASTERISK } from "src/constants";

const TRANSLATION_KEY = "ContinueDeal.CreditApp.CosignerQuestion";

const StepContentContainer = styled("div")`
    margin: auto;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 80px 22px 0;
    }
`;

interface CosignerQuestionFormData {
    hasCosigner: boolean;
    jointRelationship?: string;
}

interface CosignerQuestionProps extends BaseStepProps {
    formData: { hasCosigner: boolean; jointRelationship?: string };
    onBackClick: (data: CosignerQuestionFormData) => void;
    onNextClick: (data: CosignerQuestionFormData) => void;
}

export default function CosignerQuestion({
    formData = { hasCosigner: false },
    onNextClick,
    onBackClick,
    className,
}: CosignerQuestionProps) {
    const { t } = useTranslation();

    const [cosignerFormState, setFormState] = useState({
        jointRelationship: (formData && formData.jointRelationship) ?? "",
        hasCosigner: formData.hasCosigner ?? false,
    });

    const [error, setError] = useState("");

    const validMaritalStatus = () => {
        setError(required(cosignerFormState.jointRelationship));
        return !!cosignerFormState.jointRelationship;
    };

    const handleNextClick = () => {
        if (!cosignerFormState.hasCosigner) {
            return onNextClick({
                hasCosigner: cosignerFormState.hasCosigner,
            });
        }

        if (cosignerFormState.hasCosigner && validMaritalStatus()) {
            analyticsFEA.engine.cosignerYes();
            return onNextClick(cosignerFormState);
        }
    };

    const handleBackClick = () => {
        cosignerFormState.hasCosigner
            ? onBackClick(cosignerFormState)
            : onBackClick({
                  hasCosigner: cosignerFormState.hasCosigner,
              });
    };

    const handleChangeHasCosigner = ({
        target: { value },
    }: {
        target: {
            value: any;
        };
    }) => {
        // reset jointRelationship State if user toggles between Yes & No buttons
        value === "no"
            ? setFormState({ jointRelationship: "", hasCosigner: false })
            : setFormState({ ...cosignerFormState, hasCosigner: true });
    };

    const cosignerFields = {
        hasCosigner: {
            options: [
                { label: t(`${TRANSLATION_KEY}.hasCosignerYesLabel`), value: "yes" },
                { label: t(`${TRANSLATION_KEY}.hasCosignerNoLabel`), value: "no" },
            ],
            label: t(`${TRANSLATION_KEY}.hasCosignerLabel`),
        },
        jointRelationship: {
            options: [
                { label: t(`${TRANSLATION_KEY}.civilUnionLabel`), value: "CivilUnion" },
                { label: t(`${TRANSLATION_KEY}.regDomPRLabel`), value: "RegDomPr" },
                { label: t(`${TRANSLATION_KEY}.spouseLabel`), value: "Spouse" },
                { label: t(`${TRANSLATION_KEY}.relativeLabel`), value: "Relative" },
                { label: t(`${TRANSLATION_KEY}.otherLabel`), value: "Other" },
            ],
            label: t(`${TRANSLATION_KEY}.jointRelationshipLabel`),
        },
    };
    const { jointRelationship, hasCosigner } = cosignerFields;

    return (
        <FormContainer className={classNames("CosignerForm", className)}>
            <FormHeader className="CosignerForm__header">{t(`${TRANSLATION_KEY}.title`)}</FormHeader>
            <div className="CosignerForm__content">
                <FormControl className="CosignerForm__group">
                    <Label className="CosignerForm__label hasCosigner" htmlFor="hasCosigner">
                        {hasCosigner.label}
                        <SharedRadioGroup
                            options={hasCosigner.options}
                            aria-label="hasCosigner"
                            name="hasCosigner"
                            value={formData.hasCosigner ? "yes" : "no"}
                            onChange={handleChangeHasCosigner}
                        />
                    </Label>
                </FormControl>
                {cosignerFormState.hasCosigner && (
                    <FormControl required error={!!error} className="CosignerForm__group">
                        <Label className="CosignerForm__label jointRelationshipLabel" htmlFor="jointRelationship">
                            {jointRelationship.label}
                            {ASTERISK}
                            <Select
                                style={{ width: "300px", marginTop: "5px", marginBottom: "5px" }}
                                aria-label="jointRelationship"
                                name="jointRelationship"
                                options={jointRelationship.options}
                                onChange={(e: { target: { value: any } }) =>
                                    setFormState({ ...cosignerFormState, jointRelationship: e.target.value })
                                }
                                onBlur={(e: { target: { value: any } }) =>
                                    setFormState({ ...cosignerFormState, jointRelationship: e.target.value })
                                }
                                value={cosignerFormState.jointRelationship}
                            />
                        </Label>
                        <FieldError>{error}</FieldError>
                    </FormControl>
                )}
            </div>
            <StepContentContainer>
                <StepContent onBackClick={handleBackClick} onNextClick={handleNextClick} />
            </StepContentContainer>
        </FormContainer>
    );
}
